import * as yup from 'yup';
import i18n from '../../../../i18n';
import { pkValidation } from '../../../../utils/pkValidation';
import { PK_FORMAT_REGEX } from '../../../../utils/transform';

/**
 * Hasta dónde llega el recorrido en UNA de sus carreteras.
 *
 * Los extremos de la vía (`pkInitRoad`/`pkEndRoad`) viajan DENTRO de la fila, y
 * no sueltos en el formulario, por dos motivos: con varias carreteras cada una
 * tiene los suyos, y así `pkValidation` sigue funcionando sin tocarla — dentro
 * del test de un item de yup, `this.parent` ES el item.
 */
export interface RouteRoadRangeFormValue {
  roadId: string;
  /** PK editables del recorrido en esta carretera. */
  pkInit: string;
  pkEnd: string;
  /** Extremos de la vía, auto-rellenados al elegirla (read-only en la UI). */
  pkInitRoad: string;
  pkEndRoad: string;
}

export interface NewRouteFormValues {
  name: string;
  /** Descripción del recorrido (D2 2026-07-20): OPCIONAL, se guarda en
   *  `Order.description` de la Order gemela (entidad doble). Separada del
   *  campo `name` (antes el backend sembraba `description = name`, ver
   *  Addendum 2026-07-20 (2)). SIN campo Notas — decisión de usuario. */
  description?: string;
  concessionId: string;
  /**
   * Carreteras del recorrido (multi-carretera 2026-08-15). Antes era un
   * `roadId` escalar; un recorrido puede cubrir una troncal y su ramal, que en
   * el caso de la C-25 y la C-25z comparten kilometraje sobre trazados que se
   * separan hasta 2,76 km.
   */
  roadIds: string[];
  /** Una fila por carretera, en el mismo orden que `roadIds`. */
  roadRanges: RouteRoadRangeFormValue[];
}

/** El test de PK que comparten las dos columnas de cada fila. */
const pkField = () =>
  yup
    .string()
    .matches(PK_FORMAT_REGEX, () => i18n.t('validation:pk.format'))
    .test('pk-validation', function (value) {
      // `this.parent` es la FILA, que lleva sus propios `pkInitRoad`/`pkEndRoad`
      // — por eso cada PK se compara contra los extremos de SU carretera y no
      // contra los de la primera.
      if (
        PK_FORMAT_REGEX.test(value || '') &&
        pkValidation(value || '', this.parent)
      ) {
        return true;
      }
      return this.createError({ message: i18n.t('validation:pk.validation') });
    })
    .required(() => i18n.t('validation:required'));

// V4 FR-4.2 (2026-05-07) — `name` ("Nom del recorregut") obligatorio.
// El listing P1 + el TimerOverlay (D3) lo muestran como label primario;
// si está vacío caen al ROID canónico fallback. Para nuevos RVs el form
// fuerza el campo (no se permite Continuar sin él).
//
// Tour P5 (2026-07-07) — PKs con la MISMA validación que el LocationForm
// canónico de actuaciones (`createLocationSchema`): formato `km+metros`
// (validation:pk.format) + rango dentro de los límites de la carretera vía
// `pkValidation` (validation:pk.validation). Mensajes = keys canónicas del
// namespace `validation` (antes forms:validation.*).
export const newRouteSchema = yup.object<NewRouteFormValues>().shape({
  name: yup
    .string()
    .trim()
    .min(1, () => i18n.t('validation:required'))
    .required(() => i18n.t('validation:required')),
  // D2 (2026-07-20): opcional a propósito — NO `.required()`. Sin esta
  // entrada explícita el resolver de yup ignora el campo (unknown key) y
  // `data.description` llegaría siempre `undefined` a `onSubmit`.
  description: yup.string(),
  concessionId: yup.string().required(() => i18n.t('validation:required')),
  roadIds: yup
    .array()
    .of(yup.string().required())
    .min(1, () => i18n.t('validation:required'))
    .required(() => i18n.t('validation:required')),
  roadRanges: yup
    .array()
    .of(
      yup.object().shape({
        roadId: yup.string().required(() => i18n.t('validation:required')),
        pkInitRoad: yup.string().required(() => i18n.t('validation:required')),
        pkEndRoad: yup.string().required(() => i18n.t('validation:required')),
        pkInit: pkField(),
        pkEnd: pkField(),
      }),
    )
    .min(1, () => i18n.t('validation:required'))
    .required(() => i18n.t('validation:required')),
});
